import React, { useMemo, useRef, useState, useEffect } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./DateRangePicker.css";
import {
  startOfDay,
  endOfDay,
  subDays,
  startOfMonth,
  endOfMonth,
  subMonths,
  startOfYear,
  endOfYear,
  format
} from "date-fns";
import { es as dfEs, enUS, ptBR } from "date-fns/locale";
import { TranslationService } from "../../../services/TranslationService";

type OptionKey =
  | "today"
  | "yesterday"
  | "last7Days"
  | "lastMonth"
  | "currentMonth"
  | "currentYear"
  | "allDatesBefore"
  | "allDatesAfter"
  | "dateRange"
  | "singleDate";

type TypeDate = "fixedDay" | "rangeDate";

type Preset = {
  option: OptionKey;
  typeDate: TypeDate;
  start?: Date | null;
  end?: Date | null;
};

type Labels = Partial<Record<OptionKey, string>>;

type Props = {
  onPresetChange?: (preset: Preset) => void;
  onRangeChange?: (start: Date | null, end: Date | null) => void;
  onChange?: (val?: any) => void;
  anchorDate?: Date;
  labels?: Labels;
  options?: { option: OptionKey; typeDate: TypeDate }[];
  defaultValue?: string | Date | null;
  formatString?: string;
  /**
   * Bump this to make the picker re-read `defaultValue`. A bare `defaultValue`
   * change is ignored on purpose: consumers feed back the value they get from
   * `onRangeChange`, and re-reading it would overwrite the preset the user picked.
   */
  syncToken?: string | number;
};

// ===== Límites =====
const MIN_DATE = startOfDay(new Date(1973, 0, 1));
const MAX_DATE = endOfDay(new Date(2200, 0, 1));

// ===== i18n labels base =====
const DEFAULT_LABELS_EN: Record<OptionKey, string> = {
  today: "Today",
  yesterday: "Yesterday",
  last7Days: "Last 7 days",
  lastMonth: "Last month",
  currentMonth: "Current month",
  currentYear: "Current year",
  allDatesBefore: "Before",
  allDatesAfter: "After",
  dateRange: "Date range",
  singleDate: "Date"
};

const localeEs = {
  today: "Hoy",
  yesterday: "Ayer",
  last7Days: "Últimos 7 días",
  lastMonth: "Mes anterior",
  currentMonth: "Mes actual",
  currentYear: "Año actual",
  allDatesBefore: "Anterior a",
  allDatesAfter: "Posterior a",
  dateRange: "Rango de fechas",
  singleDate: "Fecha",
  fromLabel: "Desde",
  toLabel: "Hasta"
};

const localeEn = {
  today: "Today",
  yesterday: "Yesterday",
  last7Days: "Last 7 days",
  lastMonth: "Last month",
  currentMonth: "Current month",
  currentYear: "Current year",
  allDatesBefore: "Before",
  allDatesAfter: "After",
  dateRange: "Date range",
  singleDate: "Date",
  fromLabel: "From",
  toLabel: "To"
};

const localePt = {
  today: "Hoje",
  yesterday: "Ontem",
  last7Days: "Últimos 7 dias",
  lastMonth: "Mês anterior",
  currentMonth: "Mês atual",
  currentYear: "Ano corrente",
  allDatesBefore: "Anterior a",
  allDatesAfter: "Depois",
  dateRange: "Intervalo de datas",
  singleDate: "Data",
  fromLabel: "De",
  toLabel: "Até"
};

const LOCALE_TEXTS: Record<"es" | "en" | "pt", any> = {
  es: localeEs,
  en: localeEn,
  pt: localePt
};

const DFNS_LOCALES: Record<"es" | "en" | "pt", Locale> = {
  es: dfEs,
  en: enUS,
  pt: ptBR
};

const DATE_FORMAT_BY_LANG: Record<"es" | "en" | "pt", string> = {
  es: "dd/MM/yyyy",
  en: "MM/dd/yyyy",
  pt: "dd/MM/yyyy"
};

// Opciones (agrego singleDate)
const DEFAULT_OPTIONS: { option: OptionKey; typeDate: TypeDate }[] = [
  { option: "today", typeDate: "fixedDay" },
  { option: "yesterday", typeDate: "fixedDay" },
  { option: "singleDate", typeDate: "fixedDay" },
  { option: "last7Days", typeDate: "rangeDate" },
  { option: "lastMonth", typeDate: "rangeDate" },
  { option: "currentMonth", typeDate: "rangeDate" },
  { option: "currentYear", typeDate: "rangeDate" },
  { option: "allDatesBefore", typeDate: "rangeDate" },
  { option: "allDatesAfter", typeDate: "rangeDate" },
  { option: "dateRange", typeDate: "rangeDate" }
];

const parseYYYYMMDD = (s?: string | Date | null): Date | null => {
  if (!s) return null;
  if (s instanceof Date) return isNaN(+s) ? null : s;
  if (typeof s !== "string" || s.length !== 8) return null;
  const y = Number(s.slice(0, 4));
  const m = Number(s.slice(4, 6)) - 1;
  const d = Number(s.slice(6, 8));
  const dt = new Date(y, m, d);
  return isNaN(+dt) ? null : dt;
};

// The absolute range a preset reports, together with the state of the visible
// inputs that represent it. Keeping both in one place is what lets the component
// apply a preset in a single pass instead of reacting to its own state changes.
type ResolvedPreset = {
  rangeStart: Date | null;
  rangeEnd: Date | null;
  singleDate: Date | null;
  start: Date | null;
  end: Date | null;
};

const EMPTY_RESOLVED: ResolvedPreset = {
  rangeStart: null,
  rangeEnd: null,
  singleDate: null,
  start: null,
  end: null
};

const resolvePreset = (preset: Preset | null): ResolvedPreset => {
  if (!preset) return EMPTY_RESOLVED;

  switch (preset.option) {
    // Fixed days render in a read-only box, so no editable input holds them.
    case "today":
    case "yesterday":
      return {
        ...EMPTY_RESOLVED,
        start: preset.start ?? null,
        end: preset.end ?? null
      };
    case "singleDate": {
      const d = preset.start ?? null;
      return {
        rangeStart: null,
        rangeEnd: null,
        singleDate: d,
        start: d ? startOfDay(d) : null,
        end: d ? endOfDay(d) : null
      };
    }
    case "allDatesBefore":
      return {
        rangeStart: MIN_DATE,
        rangeEnd: preset.end ?? null,
        singleDate: null,
        start: MIN_DATE,
        end: preset.end ?? null
      };
    case "allDatesAfter":
      return {
        rangeStart: preset.start ?? null,
        rangeEnd: MAX_DATE,
        singleDate: null,
        start: preset.start ?? null,
        end: MAX_DATE
      };
    default:
      if (preset.typeDate !== "rangeDate") return EMPTY_RESOLVED;
      return {
        rangeStart: preset.start ?? null,
        rangeEnd: preset.end ?? null,
        singleDate: null,
        start: preset.start ?? null,
        end: preset.end ?? null
      };
  }
};

// Rebuilds a preset from a serialized "YYYYMMDD" or "YYYYMMDD-YYYYMMDD" value.
const presetFromValue = (value?: string | Date | null): Preset | null => {
  if (!value) return null;

  if (typeof value === "string") {
    const parts = value.split("-");

    if (parts.length === 2 && parts[0].length === 8 && parts[1].length === 8) {
      const s = parseYYYYMMDD(parts[0]);
      const e = parseYYYYMMDD(parts[1]);
      if (!s || !e) return null;

      let start = startOfDay(s);
      let end = endOfDay(e);
      if (start > end) [start, end] = [startOfDay(e), endOfDay(s)];

      return {
        option: "dateRange",
        typeDate: "rangeDate",
        start: new Date(Math.max(MIN_DATE.getTime(), start.getTime())),
        end: new Date(Math.min(MAX_DATE.getTime(), end.getTime()))
      };
    }

    if (value.length === 8) {
      const d = parseYYYYMMDD(value);
      return d
        ? {
            option: "singleDate",
            typeDate: "fixedDay",
            start: startOfDay(d),
            end: endOfDay(d)
          }
        : null;
    }

    return null;
  }

  return isNaN(+value)
    ? null
    : {
        option: "singleDate",
        typeDate: "fixedDay",
        start: startOfDay(value),
        end: endOfDay(value)
      };
};

const RangedDatepicker = (props: Props) => {
  const rawLang = (TranslationService?.currentLanguage || "en").toLowerCase();
  const lang: "es" | "en" | "pt" = (
    ["es", "en", "pt"].includes(rawLang) ? rawLang : "en"
  ) as any;

  const {
    onPresetChange,
    onRangeChange,
    anchorDate,
    labels = {},
    options = DEFAULT_OPTIONS,
    formatString,
    defaultValue,
    syncToken
  } = props;

  const emit = React.useCallback(
    (s: Date | null, e: Date | null) => {
      onRangeChange?.(s, e);
    },
    [onRangeChange]
  );

  useEffect(() => {
    registerLocale("es", DFNS_LOCALES.es);
    registerLocale("en", DFNS_LOCALES.en);
    registerLocale("pt", DFNS_LOCALES.pt);
  }, []);

  const baseTexts = LOCALE_TEXTS[lang] || localeEn;
  const i18nLabels: Record<OptionKey, string> = useMemo(() => {
    const base =
      lang === "es"
        ? { ...localeEs }
        : lang === "pt"
          ? { ...localePt }
          : { ...DEFAULT_LABELS_EN };
    return { ...base, ...labels } as Record<OptionKey, string>;
  }, [lang, labels]);

  const placeholders = useMemo(() => {
    const from =
      baseTexts.fromLabel ||
      (lang === "es" ? "Desde" : lang === "pt" ? "De" : "From");
    const to =
      baseTexts.toLabel ||
      (lang === "es" ? "Hasta" : lang === "pt" ? "Até" : "To");
    const dateOnly = lang === "es" ? "Fecha" : lang === "pt" ? "Data" : "Date";
    return {
      start: from,
      end: to,
      until: to,
      fromOnly: from,
      dateOnly
    };
  }, [baseTexts, lang]);

  const dateFormat = formatString || DATE_FORMAT_BY_LANG[lang];

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Preset | null>(null);

  const [rangeStart, setRangeStart] = useState<Date | null>(null);
  const [rangeEnd, setRangeEnd] = useState<Date | null>(null);
  const [singleDate, setSingleDate] = useState<Date | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);

  const computePreset = (option: OptionKey): Preset => {
    const baseToday = new Date();
    const anchor = anchorDate ?? baseToday;

    switch (option) {
      case "today": {
        const s = startOfDay(baseToday);
        const e = endOfDay(baseToday);
        return { option, typeDate: "fixedDay", start: s, end: e };
      }
      case "yesterday": {
        const y = subDays(baseToday, 1);
        return {
          option,
          typeDate: "fixedDay",
          start: startOfDay(y),
          end: endOfDay(y)
        };
      }
      case "singleDate": {
        const d = baseToday;
        return {
          option,
          typeDate: "fixedDay",
          start: startOfDay(d),
          end: endOfDay(d)
        };
      }
      case "last7Days": {
        const s = startOfDay(subDays(baseToday, 6));
        const e = endOfDay(baseToday);
        return { option, typeDate: "rangeDate", start: s, end: e };
      }
      case "lastMonth": {
        const lastM = subMonths(baseToday, 1);
        return {
          option,
          typeDate: "rangeDate",
          start: startOfMonth(lastM),
          end: endOfMonth(lastM)
        };
      }
      case "currentMonth": {
        return {
          option,
          typeDate: "rangeDate",
          start: startOfMonth(baseToday),
          end: endOfMonth(baseToday)
        };
      }
      case "currentYear": {
        return {
          option,
          typeDate: "rangeDate",
          start: startOfYear(baseToday),
          end: endOfYear(baseToday)
        };
      }
      case "allDatesBefore": {
        const s = MIN_DATE;
        const e = endOfDay(anchor);
        return { option, typeDate: "rangeDate", start: s, end: e };
      }
      case "allDatesAfter": {
        const s = startOfDay(anchor);
        const e = MAX_DATE;
        return { option, typeDate: "rangeDate", start: s, end: e };
      }
      case "dateRange":
      default:
        return {
          option: "dateRange",
          typeDate: "rangeDate",
          start: null,
          end: null
        };
    }
  };

  // Single entry point for changing the selection. `shouldEmit` is what separates a
  // user action (the parent must hear about it) from an external sync (it must not,
  // or the parent would receive back the very value it just pushed in).
  const applyPreset = (preset: Preset | null, shouldEmit: boolean) => {
    const resolved = resolvePreset(preset);
    setSelected(preset);
    setRangeStart(resolved.rangeStart);
    setRangeEnd(resolved.rangeEnd);
    setSingleDate(resolved.singleDate);
    if (shouldEmit) {
      emit(resolved.start, resolved.end);
    }
  };

  const handleSelect = (
    opt: OptionKey,
    dates?: { start?: Date | null; end?: Date | null }
  ) => {
    const base = computePreset(opt);
    const preset = dates ? { ...base, ...dates } : base;
    applyPreset(preset, true);
    onPresetChange?.(preset);
  };

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (
        !(
          containerRef.current &&
          containerRef.current.contains(e.target as Node)
        )
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Reads `defaultValue` on mount and every time `syncToken` changes, never emitting:
  // an external value is not a user action. `defaultValue` is intentionally absent
  // from the deps — consumers pass back the value they received from `onRangeChange`,
  // so reacting to it would clobber the preset the user just selected.
  useEffect(() => {
    applyPreset(presetFromValue(defaultValue), false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncToken]);

  const showRange =
    selected?.typeDate === "rangeDate" &&
    selected?.option !== "allDatesBefore" &&
    selected?.option !== "allDatesAfter";

  const showBeforeSingleEnd = selected?.option === "allDatesBefore";
  const showAfterSingleStart = selected?.option === "allDatesAfter";
  const showSingleDayReadonly =
    selected?.option === "today" || selected?.option === "yesterday";
  const showSingleEditable = selected?.option === "singleDate";

  return (
    <div ref={containerRef} style={styles.wrapper as React.CSSProperties}>
      {/* Selector (izquierda) */}
      <div
        id="container-input"
        style={styles.container as React.CSSProperties}
        onClick={() => setOpen((v) => !v)}
        role="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        tabIndex={0}
        onKeyDown={(e) =>
          (e.key === "Enter" || e.key === " ") && setOpen((v) => !v)
        }
      >
        <span style={{ flex: 1 }}>
          {selected ? i18nLabels[selected.option] : i18nLabels["dateRange"]}
        </span>
        <span aria-hidden>▾</span>
      </div>

      {open && (
        <div style={styles.menu as React.CSSProperties} role="listbox">
          {options.map(({ option }) => (
            <div
              key={option}
              role="option"
              aria-selected={selected?.option === option}
              onClick={() => handleSelect(option)}
              style={{
                ...styles.item,
                ...(selected?.option === option ? styles.itemActive : {})
              }}
            >
              {i18nLabels[option]}
            </div>
          ))}
        </div>
      )}

      {/* Panel derecho */}
      <>
        {showRange ? (
          <div style={{ display: "flex", gap: 8 }}>
            <DatePicker
              className="form-control rdp-input"
              calendarClassName="rdp-cal"
              popperClassName="rdp-popper"
              wrapperClassName="rdp-wrap"
              selected={rangeStart}
              onChange={(date) => {
                const d = (date as Date) ?? null;
                setRangeStart(d);
                setRangeEnd((prev) => (prev && d && prev < d ? d : prev));
                const finalEnd = ((prev) => (prev && d && prev < d ? d : prev))(
                  rangeEnd as any
                );
                onRangeChange?.(d, finalEnd);
              }}
              selectsStart
              startDate={rangeStart}
              endDate={rangeEnd}
              placeholderText={placeholders.start}
              maxDate={rangeEnd || undefined}
              isClearable
              onFocus={() => setOpen(false)}
              locale={lang}
              dateFormat={dateFormat}
            />
            <DatePicker
              className="form-control rdp-input"
              calendarClassName="rdp-cal"
              popperClassName="rdp-popper"
              wrapperClassName="rdp-wrap"
              selected={rangeEnd}
              onChange={(date) => {
                const d = (date as Date) ?? null;
                setRangeEnd(d);
                onRangeChange?.(rangeStart, d);
              }}
              selectsEnd
              startDate={rangeStart}
              endDate={rangeEnd}
              minDate={rangeStart || undefined}
              placeholderText={placeholders.end}
              isClearable
              onFocus={() => setOpen(false)}
              locale={lang}
              dateFormat={dateFormat}
            />
          </div>
        ) : showBeforeSingleEnd ? (
          <div>
            <DatePicker
              className="form-control rdp-input"
              calendarClassName="rdp-cal"
              popperClassName="rdp-popper"
              wrapperClassName="rdp-wrap"
              selected={rangeEnd}
              onChange={(date) => {
                const d = (date as Date) ?? null;
                const endVal = d ? endOfDay(d) : null;
                setRangeStart(MIN_DATE);
                setRangeEnd(endVal);
                onRangeChange?.(MIN_DATE, endVal);
              }}
              selectsEnd
              startDate={MIN_DATE}
              endDate={rangeEnd}
              minDate={MIN_DATE}
              maxDate={MAX_DATE}
              placeholderText={placeholders.until}
              isClearable
              onFocus={() => setOpen(false)}
              locale={lang}
              dateFormat={dateFormat}
            />
          </div>
        ) : showAfterSingleStart ? (
          <div>
            <DatePicker
              className="form-control rdp-input"
              calendarClassName="rdp-cal"
              popperClassName="rdp-popper"
              wrapperClassName="rdp-wrap"
              selected={rangeStart}
              onChange={(date) => {
                const d = (date as Date) ?? null;
                const startVal = d ? startOfDay(d) : null;
                setRangeStart(startVal);
                setRangeEnd(MAX_DATE);
                onRangeChange?.(startVal, MAX_DATE);
              }}
              selectsStart
              startDate={rangeStart ?? undefined}
              endDate={MAX_DATE}
              minDate={MIN_DATE}
              maxDate={MAX_DATE}
              placeholderText={placeholders.fromOnly}
              isClearable
              onFocus={() => setOpen(false)}
              locale={lang}
              dateFormat={dateFormat}
            />
          </div>
        ) : showSingleEditable ? (
          <div>
            <DatePicker
              className="form-control rdp-input"
              calendarClassName="rdp-cal"
              popperClassName="rdp-popper"
              wrapperClassName="rdp-wrap"
              selected={singleDate}
              onChange={(date) => {
                const d = (date as Date) ?? null;
                setSingleDate(d);
                onRangeChange?.(
                  d ? startOfDay(d) : null,
                  d ? endOfDay(d) : null
                );
              }}
              placeholderText={placeholders.dateOnly}
              isClearable
              onFocus={() => setOpen(false)}
              locale={lang}
              dateFormat={dateFormat}
            />
          </div>
        ) : showSingleDayReadonly ? (
          <input
            type="text"
            disabled
            value={selected?.start ? format(selected.start, dateFormat) : ""}
            aria-label={
              selected?.option === "today"
                ? i18nLabels.today
                : i18nLabels.yesterday
            }
            style={styles.calendarInput as React.CSSProperties}
          />
        ) : (
          <div style={styles.placeholder as React.CSSProperties} />
        )}
      </>
    </div>
  );
};

const styles = {
  wrapper: {
    display: "inline-grid",
    gridTemplateColumns: "1fr 2fr",
    gap: 8,
    alignItems: "start",
    width: "max-content",
    verticalAlign: "top",
    position: "relative"
  },
  container: {
    background: "#fff",
    cursor: "pointer",
    padding: "7px 10px",
    display: "flex",
    alignItems: "center",
    border: "1px solid #D7DEEC",
    borderRadius: "0.375rem",
    fontSize: "13px",
    lineHeight: "22px",
    userSelect: "none",
    justifyContent: "space-between",
    minWidth: "100px"
  },
  menu: {
    position: "absolute",
    top: "100%",
    left: 0,
    marginTop: 6,
    minWidth: "120px",
    background: "#fff",
    border: "1px solid #D7DEEC",
    borderRadius: "8px",
    boxShadow: "0 8px 24px rgba(18, 38, 63, 0.12)",
    zIndex: 10,
    padding: 6
  },
  item: {
    padding: "8px 10px",
    borderRadius: 6,
    cursor: "pointer"
  },
  itemActive: {
    background: "#F0F4FF",
    fontWeight: 600
  },
  calendarInput: {
    width: "100%",
    fontSize: 13,
    lineHeight: "22px",
    padding: "6px 8px",
    border: "1px solid #E3E8F5",
    borderRadius: 6,
    background: "#F5F7FD",
    color: "#6B7280"
  },
  placeholder: {
    width: "100%",
    minHeight: 24
  }
} as const;

export default RangedDatepicker;
